import { useT } from '@planetra/i18n';
import { useCallback } from 'react';
import type { ApiFailure, ApiFailureKind } from './errors';

/**
 * A failure, in words (P0-WEB-04).
 *
 * The counterpart to `classifyApiError`: that decides what happened, this
 * decides what a person is told. Split because the classifier has to be usable
 * from a loader, where there are no hooks, and because the copy is worth
 * testing without a network.
 *
 * This never renders the API's own `message`. Laravel resolves `__()` against
 * the application locale and apps/api negotiates none, so those strings are
 * English regardless of what the user chose — showing them would put untranslated
 * text in a Malay UI at the moment things are already going wrong. The
 * exception is `fieldErrors`, which forms take directly; see `errors.ts`.
 */

export interface ApiErrorMessage {
  readonly title: string;
  readonly description: string;
  /** Whether offering a retry button is honest. It is not, for a 403. */
  readonly retryable: boolean;
}

/**
 * Codes the API returns that deserve their own words, mapped to literal keys.
 *
 * A lookup table rather than a template string, because `t` is typed against
 * the catalogue: `t(\`errors:api.codes.${code}.title\`)` would not typecheck,
 * and the usual fix — widening the augmentation — turns off key checking for
 * the whole app. An unlisted code simply falls through to its `kind`.
 *
 * The OAuth codes arrive as a `?error=` query parameter rather than in a
 * response body (P0-PLAT-08 redirects to {PORTAL_URL}/sign-in?error=CODE), so
 * P0-WEB-06's sign-in page resolves them through this same table.
 */
const CODE_KEYS = {
  session_revoked: 'errors:api.codes.session_revoked',
  oauth_failed: 'errors:api.codes.oauth_failed',
  oauth_email_unverified: 'errors:api.codes.oauth_email_unverified',
  oauth_account_exists: 'errors:api.codes.oauth_account_exists',
} as const;

export type KnownErrorCode = keyof typeof CODE_KEYS;

export function isKnownErrorCode(code: string | undefined): code is KnownErrorCode {
  return code !== undefined && code in CODE_KEYS;
}

/** Retrying a 403 or a 422 just repeats the same answer. */
const RETRYABLE: ReadonlySet<ApiFailureKind> = new Set<ApiFailureKind>([
  'offline',
  'server',
  'rateLimited',
  'unknown',
]);

export function useApiError(): (failure: ApiFailure) => ApiErrorMessage {
  const { t } = useT();

  return useCallback(
    (failure: ApiFailure): ApiErrorMessage => {
      const retryable = RETRYABLE.has(failure.kind);

      // A recognised code is more specific than the status that carried it —
      // `session_revoked` and a plain 401 are both `unauthenticated`, and only
      // one of them should say "signed out from another device".
      if (isKnownErrorCode(failure.code)) {
        const key = CODE_KEYS[failure.code];
        return {
          title: t(`${key}.title` as 'errors:api.codes.session_revoked.title'),
          description: t(`${key}.description` as 'errors:api.codes.session_revoked.description'),
          retryable,
        };
      }

      if (failure.kind === 'rateLimited' && failure.retryAfterSeconds !== undefined) {
        return {
          title: t('errors:api.rateLimited.title'),
          description: t('errors:api.rateLimited.descriptionAfter', {
            seconds: failure.retryAfterSeconds,
          }),
          retryable,
        };
      }

      return {
        title: t(KIND_TITLES[failure.kind]),
        description: t(KIND_DESCRIPTIONS[failure.kind]),
        retryable,
      };
    },
    [t],
  );
}

/**
 * Written out per kind rather than interpolated, for the same reason as
 * `CODE_KEYS`: a missing key becomes a compile error here instead of a string
 * that renders as its own name, which reads like a deliberate label until
 * somebody looks closely.
 */
const KIND_TITLES = {
  cancelled: 'errors:api.cancelled.title',
  offline: 'errors:api.offline.title',
  unauthenticated: 'errors:api.unauthenticated.title',
  forbidden: 'errors:api.forbidden.title',
  notFound: 'errors:api.notFound.title',
  conflict: 'errors:api.conflict.title',
  gone: 'errors:api.gone.title',
  validation: 'errors:api.validation.title',
  rateLimited: 'errors:api.rateLimited.title',
  server: 'errors:api.server.title',
  unknown: 'errors:api.unknown.title',
} as const satisfies Record<ApiFailureKind, string>;

const KIND_DESCRIPTIONS = {
  cancelled: 'errors:api.cancelled.description',
  offline: 'errors:api.offline.description',
  unauthenticated: 'errors:api.unauthenticated.description',
  forbidden: 'errors:api.forbidden.description',
  notFound: 'errors:api.notFound.description',
  conflict: 'errors:api.conflict.description',
  gone: 'errors:api.gone.description',
  validation: 'errors:api.validation.description',
  rateLimited: 'errors:api.rateLimited.description',
  server: 'errors:api.server.description',
  unknown: 'errors:api.unknown.description',
} as const satisfies Record<ApiFailureKind, string>;
